import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import { getUserProfile } from "../../services/api/userApi";
import {
  MECHANIC_FIELD_OPTIONS,
  MECHANIC_FIELD_SERVICE_WORKER,
  getMyMechanicProfile,
  registerMechanic,
} from "../../services/api/mechanicApi";
import { extractErrorMessage } from "../../services/api/errorHandler";
import { toEnglishDigits } from "../Login/components/loginUtils";
import {
  AlertIcon,
  AuthIconBase,
  BackArrowIcon,
  CheckIcon,
  PhoneIcon,
  SpinnerIcon,
} from "../Login/components/AuthIcons";
import MapPicker from "./MapPicker";
import { fetchReverseGeocode, shortenLocation } from "./locationUtils";

// Minimal page-local SVG icons (same 24px stroke style as AuthIcons).
function StoreIcon(props) {
  return (
    <AuthIconBase {...props}>
      <path d="M3 9.5 4.6 4h14.8L21 9.5" />
      <path d="M3 9.5a2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 3-2.45" />
      <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
      <path d="M9 20v-5h6v5" />
    </AuthIconBase>
  );
}

function LocationIcon(props) {
  return (
    <AuthIconBase {...props}>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </AuthIconBase>
  );
}

function WrenchBadgeIcon(props) {
  return (
    <AuthIconBase {...props}>
      <path d="M14.7 6.3a4.5 4.5 0 0 0-6 6L3 18l3 3 5.7-5.7a4.5 4.5 0 0 0 6-6L14 13l-3-3z" />
    </AuthIconBase>
  );
}

function ChevronDownIcon(props) {
  return (
    <AuthIconBase size={16} {...props} className={`swr-dropdown-chevron ${props.className || ""}`.trim()}>
      <path d="m6 9 6 6 6-6" />
    </AuthIconBase>
  );
}

// Custom-styled "حوزه کاری" dropdown — frontend presentation only.
// The native <select id="swr-field"> stays mounted (visually hidden) so the
// existing label association, validation, and the exact `field` payload are
// untouched. This button + listbox mirrors the SAME value/options and is
// what the user sees and touches. Same options, same values, RTL.
function WorkFieldDropdown({
  value,
  options,
  onChange,
  disabled,
  hasError,
  describedBy,
  triggerRef,
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(value);
  const rootRef = useRef(null);
  const selected = options.find((opt) => opt.value === value) || null;

  const openMenu = () => {
    setHighlight(value);
    setOpen(true);
  };

  // Close on outside tap/click or Escape; a single subtle behavior only.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
    };
    const onKeyDown = (ev) => {
      if (ev.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open ]);

  const moveHighlight = (dir) => {
    const idx = options.findIndex((opt) => opt.value === highlight);
    const next = idx === -1 ? 0 : (idx + dir + options.length) % options.length;
    setHighlight(options[next].value);
  };

  const handleTriggerKeyDown = (ev) => {
    if (disabled) return;
    if (!open) {
      if (
        ev.key === "ArrowDown" ||
        ev.key === "ArrowUp" ||
        ev.key === "Enter" ||
        ev.key === " " ||
        ev.key === "Spacebar"
      ) {
        ev.preventDefault();
        openMenu();
      }
      return;
    }
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      moveHighlight(1);
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      moveHighlight(-1);
    } else if (ev.key === "Home") {
      ev.preventDefault();
      setHighlight(options[0].value);
    } else if (ev.key === "End") {
      ev.preventDefault();
      setHighlight(options[options.length - 1].value);
    } else if (ev.key === "Enter" || ev.key === " " || ev.key === "Spacebar") {
      ev.preventDefault();
      if (highlight) onChange(highlight);
      setOpen(false);
    } else if (ev.key === "Escape") {
      ev.preventDefault();
      setOpen(false);
    } else if (ev.key === "Tab") {
      setOpen(false);
    }
  };

  const choose = (next) => {
    onChange(next);
    setOpen(false);
    triggerRef?.current?.focus();
  };

  return (
    <div className="swr-dropdown" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`swr-dropdown-trigger${open ? " swr-dropdown-trigger-open" : ""}${
          hasError ? " swr-dropdown-trigger-error" : ""
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selected ? selected.label : "انتخاب"}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (open) setOpen(false);
          else openMenu();
        }}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="swr-dropdown-value">{selected ? selected.label : ""}</span>
        <ChevronDownIcon />
      </button>
      {open && (
        <ul className="swr-dropdown-menu" role="listbox">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            const isHighlight = opt.value === highlight;
            return (
              <li
                key={opt.value}
                id={`swr-field-option-${opt.value}`}
                role="option"
                aria-selected={isSelected}
                className={`swr-dropdown-option${
                  isSelected ? " swr-dropdown-option-selected" : ""
                }${isHighlight ? " swr-dropdown-option-highlight" : ""}`}
                onClick={() => choose(opt.value)}
                onMouseEnter={() => setHighlight(opt.value)}
              >
                <span>{opt.label}</span>
                {isSelected && <CheckIcon size={16} className="swr-dropdown-check" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Optional landline ("شماره موبایل ثابت"): Persian/Arabic digits accepted, stored Latin.
// Accepts 0-prefixed Iranian fixed/mobile numbers, 8–11 digits total.
function isValidLandline(value) {
  return /^0\d{7,10}$/.test(value);
}

function ServiceWorkerRegister() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const submittingRef = useRef(false);

  const [shopName, setShopName] = useState("");
  const [address, setAddress] = useState("");
  const [landline, setLandline] = useState("");

  // Work area ("حوزه کاری") — the existing backend `field` key
  // (MechanicField enum, REQUIRED by POST /mechanics/register).
  // Defaults to SERVICE_WORKER to preserve the page's previous payload;
  // the user can switch to any exact enum value from the selector below.
  const [workField, setWorkField] = useState(MECHANIC_FIELD_SERVICE_WORKER);

  // Map-based location selector (REQUIRED field "آدرس در نقشه").
  // `picked` only ever holds a point the user explicitly tapped/confirmed
  // in the picker — never a hardcoded value. It is submitted inside the
  // existing backend `address` string (the only location field the
  // read-only backend provides). `pickedLabel` is the short human-readable
  // summary built from the REAL reverse-lookup response — raw coordinates
  // are kept internal and never displayed to the user.
  const [picked, setPicked] = useState(null);
  const [pickedLabel, setPickedLabel] = useState("");
  const [labelLoading, setLabelLoading] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const lookupSeq = useRef(0);

  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [identity, setIdentity] = useState({ firstName: "", lastName: "", workPhone: "" });
  // Focus target of the visible "حوزه کاری" dropdown trigger.
  const fieldTriggerRef = useRef(null);

  useEffect(() => {
    document.title = "ثبت نام سرویس کار | تسمه";
    return () => {
      document.title = "تسمه";
    };
  }, []);

  // Best-effort prefill of identity (firstName/lastName/workPhone) from the
  // current user profile; detect an existing mechanic registration.
  // A missing profile name NEVER blocks registration — the submit payload
  // falls back to the shop name (see handleSubmit).
  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      setBootLoading(true);
      try {
        const profileRes = await getUserProfile();
        if (!cancelled && profileRes?.success && profileRes?.data) {
          const p = profileRes.data;
          setIdentity({
            firstName: (p.firstName || user?.firstName || "").trim(),
            lastName: (p.lastName || user?.lastName || "").trim(),
            workPhone: (p.phone || user?.phone || "").trim(),
          });
        } else if (!cancelled) {
          setIdentity({
            firstName: (user?.firstName || "").trim(),
            lastName: (user?.lastName || "").trim(),
            workPhone: (user?.phone || "").trim(),
          });
        }
      } catch {
        if (!cancelled) {
          setIdentity({
            firstName: (user?.firstName || "").trim(),
            lastName: (user?.lastName || "").trim(),
            workPhone: (user?.phone || "").trim(),
          });
        }
      }
      try {
        const mechRes = await getMyMechanicProfile();
        if (!cancelled && mechRes?.success && mechRes?.data) {
          setAlreadyRegistered(true);
          setNotice("شما قبلاً به‌عنوان سرویس کار ثبت شده‌اید.");
          try {
            window.localStorage.setItem("tasmeh:isMechanic", "true");
            window.sessionStorage.setItem("tasmeh:isMechanic", "true");
            window.localStorage.setItem("tasmeh:lastSection", "serviceWorker");
            window.sessionStorage.setItem("tasmeh:lastSection", "serviceWorker");
            try {
              window.dispatchEvent(new Event("tasmeh:nav-changed"));
            } catch {}
          } catch {}
        }
      } catch {
        // 404 = not registered yet — the normal case, stay on the form.
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    if (formError) setFormError("");
  };

  const handleLandlineChange = (e) => {
    const normalized = toEnglishDigits(e.target.value).replace(/\D/g, "").slice(0, 11);
    setLandline(normalized);
    clearFieldError("landline");
  };

  // Resolve the short human-readable summary for a picked point from the
  // REAL reverse-lookup response. Never invents or hardcodes address data:
  // failures leave the label empty and the UI falls back to a neutral
  // "selected" status (coordinates stay internal-only).
  const resolvePickedLabel = async (point) => {
    const seq = lookupSeq.current + 1;
    lookupSeq.current = seq;
    setLabelLoading(true);
    try {
      const data = await fetchReverseGeocode(point.lat, point.lng);
      if (lookupSeq.current !== seq) return;
      setPickedLabel(shortenLocation(data));
    } finally {
      if (lookupSeq.current === seq) setLabelLoading(false);
    }
  };

  const handleMapConfirm = (point) => {
    setPicked(point);
    setPickedLabel("");
    setMapOpen(false);
    clearFieldError("mapLocation");
    resolvePickedLabel(point);
  };

  // Only `نام مجموعه`, `حوزه کاری`, and `آدرس در نقشه` are required. The manual
  // address and landline are OPTIONAL and never block submission.
  const validate = () => {
    const errors = {};
    if (!shopName.trim()) {
      errors.shopName = "نام مجموعه را وارد کنید";
    } else if (shopName.trim().length < 2) {
      errors.shopName = "نام مجموعه باید حداقل ۲ حرف باشد";
    }
    // `field` is REQUIRED by the existing backend (MechanicField enum), so
    // the selection must always be one of the exact backend values.
    if (!MECHANIC_FIELD_OPTIONS.some((opt) => opt.value === workField)) {
      errors.workField = "حوزه کاری را انتخاب کنید";
    }
    if (!picked) {
      errors.mapLocation = "موقعیت روی نقشه را انتخاب کنید";
    }
    const normalizedLandline = toEnglishDigits(landline).trim();
    if (normalizedLandline && !isValidLandline(normalizedLandline)) {
      errors.landline = "فرمت شماره ثابت نادرست است (مثال: 02112345678)";
    }
    setFieldErrors(errors);
    return { errors, normalizedLandline };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current || loading || bootLoading || alreadyRegistered) return;

    setFormError("");
    setNotice("");
    const { errors, normalizedLandline } = validate();
    if (Object.keys(errors).length > 0) return;

    // NOTE: registration MUST NOT require a completed UserPanel profile.
    // Missing first/last names fall back to the user's own shop name so the
    // existing backend contract (non-empty firstName/lastName/workPhone)
    // stays satisfied without blocking, warning, or redirecting.
    const shopTrimmed = shopName.trim();

    submittingRef.current = true;
    setLoading(true);
    try {
      // Exact payload of the existing POST /mechanics/register endpoint.
      // `shopName` is forwarded for forward-compatibility; the current
      // backend ignores it (no matching column) and persists the rest.
      // Location rides in `address` — the only location field the backend
      // model provides (free text): typed address plus, when the user
      // picked a map point, a coordinates line.
      const locationLine = picked
        ? `موقعیت نقشه: ${picked.lat.toFixed(6)}, ${picked.lng.toFixed(6)}`
        : "";
      const composedAddress = [address.trim(), locationLine].filter(Boolean).join("\n");
      const payload = {
    firstName: identity.firstName || shopTrimmed,
    lastName: identity.lastName || shopTrimmed,
    workPhone: identity.workPhone,
    // Existing backend `field` key (MechanicField enum) — the value comes
    // straight from the "حوزه کاری" selector, submitted verbatim.
    field: workField,

    shopName: shopTrimmed,
    workshopName: shopTrimmed,

    ...(composedAddress ? {
        address: composedAddress
    } : {}),

    ...(normalizedLandline ? {
        landline: normalizedLandline
    } : {}),

    ...(picked ? {
        latitude: picked.lat,
        longitude: picked.lng
    } : {})
};
      const response = await registerMechanic(payload);
      if (response?.success) {
        try {
          window.localStorage.setItem("tasmeh:isMechanic", "true");
          window.sessionStorage.setItem("tasmeh:isMechanic", "true");
          window.localStorage.setItem("tasmeh:lastSection", "serviceWorker");
          window.sessionStorage.setItem("tasmeh:lastSection", "serviceWorker");
          try {
            window.dispatchEvent(new Event("tasmeh:nav-changed"));
          } catch {}
        } catch {}
        setNotice("ثبت نام شما با موفقیت انجام شد؛ در حال انتقال به داشبورد سرویس کار...");
        // Auth state untouched — token/user preserved, then follow the
        // existing flow into the service-worker dashboard.
        window.setTimeout(() => {
          navigate("/ServiceDashbord", { replace: true });
        }, 1200);
      } else {
        setFormError(
          extractErrorMessage({ response: { data: response } }, "خطا در ثبت نام. لطفاً دوباره تلاش کنید")
        );
      }
    } catch (err) {
      setFormError(extractErrorMessage(err, "خطا در ثبت نام. لطفاً دوباره تلاش کنید"));
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <div className="auth-page swr" dir="rtl" lang="fa">
      <div className="auth-wrap">
        <div className="auth-brand">
          <span className="auth-brand-disc" aria-hidden="true">
            <WrenchBadgeIcon size={26} />
          </span>
          <span className="auth-brand-name">تسمه</span>
          <span className="auth-brand-tag">یادآور سرویس خودرو</span>
        </div>

        <main className="auth-card" role="main">
          <button
            className="auth-back"
            type="button"
            onClick={() => navigate("/UserPanel")}
            aria-label="بازگشت به پنل کاربری"
          >
            <BackArrowIcon size={20} />
          </button>
          <div className="auth-card-icon" aria-hidden="true">
            <WrenchBadgeIcon size={26} />
          </div>
          <h1 className="auth-title">ثبت نام به عنوان سرویس کار</h1>
          <p className="auth-subtitle">مشخصات مجموعه خود را وارد کنید تا در تسمه ثبت شود</p>

          <div className="auth-body">
            {bootLoading ? (
              <p className="auth-alert" role="status">
                <SpinnerIcon size={18} />
                <span>در حال بارگذاری اطلاعات...</span>
              </p>
            ) : alreadyRegistered ? (
              <>
                <p className="auth-alert auth-alert-success" role="status">
                  <CheckIcon size={18} />
                  <span>{notice || "شما قبلاً به‌عنوان سرویس کار ثبت شده‌اید."}</span>
                </p>
                <button
                  className="auth-btn auth-btn-primary"
                  type="button"
                  onClick={() => navigate("/ServiceDashbord")}
                >
                  ورود به داشبورد سرویس کار
                </button>
                <button
                  className="auth-btn auth-btn-ghost"
                  type="button"
                  onClick={() => navigate("/UserPanel")}
                >
                  بازگشت به پنل کاربری
                </button>
              </>
            ) : (
              <form className="auth-form" onSubmit={handleSubmit} noValidate>
                <div className="auth-field">
                  <label className="auth-label" htmlFor="swr-shop">
                    <StoreIcon size={17} />
                    نام مجموعه
                    <span className="auth-required" aria-hidden="true">*</span>
                  </label>
                  <div className="auth-input-wrap">
                    <input
                      id="swr-shop"
                      className={`auth-input${fieldErrors.shopName ? " auth-input-error" : ""}`}
                      type="text"
                      placeholder="مثال: تعمیرگاه تسمه"
                      value={shopName}
                      onChange={(ev) => {
                        setShopName(ev.target.value);
                        clearFieldError("shopName");
                      }}
                      disabled={loading}
                      maxLength={80}
                      autoFocus
                      required
                      aria-required="true"
                      aria-invalid={!!fieldErrors.shopName}
                    />
                  </div>
                  {fieldErrors.shopName ? (
                    <p className="auth-field-error" role="alert">{fieldErrors.shopName}</p>
                  ) : (
                    <p className="auth-hint">نام مجموعه الزامی است</p>
                  )}
                </div>

                <div className="auth-field">
                  <label className="auth-label" htmlFor="swr-field">
                    <WrenchBadgeIcon size={17} />
                    حوزه کاری
                    <span className="auth-required" aria-hidden="true">*</span>
                  </label>
                  <div className="auth-input-wrap">
                    {/* Native select stays mounted (visually hidden) so the
                        label association, validation, and exact backend
                        `field` values are preserved; the custom dropdown
                        below is the visible control bound to the same state. */}
                    <select
                      id="swr-field"
                      className="swr-native-select"
                      value={workField}
                      onChange={(ev) => {
                        setWorkField(ev.target.value);
                        clearFieldError("workField");
                      }}
                      onFocus={() => fieldTriggerRef.current?.focus()}
                      disabled={loading}
                      required
                      tabIndex={-1}
                      aria-required="true"
                      aria-invalid={!!fieldErrors.workField}
                    >
                      {MECHANIC_FIELD_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <WorkFieldDropdown
                      value={workField}
                      options={MECHANIC_FIELD_OPTIONS}
                      onChange={(next) => {
                        setWorkField(next);
                        clearFieldError("workField");
                      }}
                      disabled={loading}
                      hasError={!!fieldErrors.workField}
                      describedBy={fieldErrors.workField ? "swr-field-error" : "swr-field-hint"}
                      triggerRef={fieldTriggerRef}
                    />
                  </div>
                  {fieldErrors.workField ? (
                    <p className="auth-field-error" id="swr-field-error" role="alert">{fieldErrors.workField}</p>
                  ) : (
                    <p className="auth-hint" id="swr-field-hint">حوزه فعالیت مجموعه را انتخاب کنید</p>
                  )}
                </div>

                <div className="auth-field">
                  <span className="auth-label" id="swr-location-label">
                    <LocationIcon size={17} />
                    آدرس در نقشه
                    <span className="auth-required" aria-hidden="true">*</span>
                  </span>
                  {picked ? (
                    <div className="swr-picked" aria-live="polite">
                      <span className="swr-picked-dot" aria-hidden="true" />
                      <span className="swr-picked-coords">
                        {labelLoading
                          ? "در حال دریافت آدرس..."
                          : pickedLabel || "موقعیت انتخاب شد"}
                      </span>
                      <button
                        className="swr-picked-change"
                        type="button"
                        onClick={() => setMapOpen(true)}
                        disabled={loading}
                      >
                        تغییر
                      </button>
                      <button
                        className="swr-picked-remove"
                        type="button"
                        onClick={() => {
                          setPicked(null);
                          setPickedLabel("");
                          clearFieldError("mapLocation");
                        }}
                        disabled={loading}
                        aria-label="حذف موقعیت انتخاب‌شده"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <path d="M6 6l12 12M18 6 6 18" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <button
                      className={`auth-btn auth-btn-ghost swr-map-open${fieldErrors.mapLocation ? " swr-map-open-error" : ""}`}
                      type="button"
                      onClick={() => setMapOpen(true)}
                      disabled={loading}
                      aria-describedby={fieldErrors.mapLocation ? "map-location-error" : undefined}
                    >
                      <LocationIcon size={18} />
                      انتخاب موقعیت روی نقشه
                    </button>
                  )}
                  {fieldErrors.mapLocation && !picked ? (
                    <p id="map-location-error" className="auth-field-error" role="alert">{fieldErrors.mapLocation}</p>
                  ) : (
                    <p className="auth-hint">موقعیت مجموعه الزامی است؛ برای تغییر، نقشه را دوباره باز کنید</p>
                  )}
                </div>

                <div className="auth-field">
                  <label className="auth-label" htmlFor="swr-address">
                    <LocationIcon size={17} />
                    آدرس به صورت دستی و متنی
                    <span className="auth-optional">(اختیاری)</span>
                  </label>
                  <div className="auth-input-wrap">
                    <textarea
                      id="swr-address"
                      className="auth-input auth-textarea"
                      placeholder="مثال: تهران، خیابان آزادی، پلاک ۱۲"
                      value={address}
                      onChange={(ev) => setAddress(ev.target.value)}
                      disabled={loading}
                      maxLength={500}
                      rows={2}
                    />
                  </div>
                </div>

                <div className="auth-field">
                  <label className="auth-label" htmlFor="swr-landline">
                    <PhoneIcon size={17} />
                    شماره ثابت
                    <span className="auth-optional">(اختیاری)</span>
                  </label>
                  <div className="auth-input-wrap">
                    <input
                      id="swr-landline"
                      className={`auth-input auth-input-ltr${fieldErrors.landline ? " auth-input-error" : ""}`}
                      type="tel"
                      inputMode="numeric"
                      dir="ltr"
                      placeholder="02112345678"
                      value={landline}
                      onChange={handleLandlineChange}
                      disabled={loading}
                      maxLength={11}
                      aria-invalid={!!fieldErrors.landline}
                    />
                  </div>
                  {fieldErrors.landline ? (
                    <p className="auth-field-error" role="alert">{fieldErrors.landline}</p>
                  ) : (
                    <p className="auth-hint">شماره ثابت مجموعه، بدون فاصله وارد شود</p>
                  )}
                </div>

                {formError && (
                  <p className="auth-alert auth-alert-error" role="alert">
                    <AlertIcon size={18} />
                    <span>{formError}</span>
                  </p>
                )}

                {notice && !formError && (
                  <p className="auth-alert auth-alert-success" role="status">
                    <CheckIcon size={18} />
                    <span>{notice}</span>
                  </p>
                )}

                <button className="auth-btn auth-btn-primary" type="submit" disabled={loading}>
                  {loading && <SpinnerIcon />}
                  {loading ? "در حال ثبت..." : "ثبت نام"}
                </button>

                <button
                  className="auth-btn auth-btn-ghost"
                  type="button"
                  onClick={() => navigate("/UserPanel")}
                  disabled={loading}
                >
                  انصراف و بازگشت
                </button>
              </form>
            )}
          </div>
        </main>

        <p className="auth-footer">ثبت نام شما پس از تایید، در داشبورد سرویس کار فعال می‌شود</p>
      </div>

      <MapPicker
        open={mapOpen}
        initial={picked}
        onConfirm={handleMapConfirm}
        onClose={() => setMapOpen(false)}
      />
    </div>
  );
}

export default ServiceWorkerRegister;
